//* Libraries Imports
import { motion } from "framer-motion";
import React, { useState, useCallback, useEffect, memo } from "react";

//* Components Imports
import { Modal, Button, Spinner } from "react-bootstrap";

//* Icon Imports
import {
  FaCertificate,
  FaDownload,
  FaExclamationTriangle,
} from "react-icons/fa";

//? Styles Imports
import "./view-resul-modal.scss";

//* Constants Imports
import { STORAGE_URL } from "../../../../constants/consts";
import Icon from "../../../../components/common/Icon";

type CertificateData = {
  url: string;
  filename: string;
};

type CertificateModalProps = {
  show: boolean;
  onHide: () => void;
  certificate: CertificateData | null;
  studentName: string;
  onDownload?: () => void;
};

type LoadingState = "idle" | "loading" | "success" | "error";

export const CertificateModal: React.FC<CertificateModalProps> = memo(
  ({ show, onHide, certificate, studentName, onDownload }) => {
    const [loadingState, setLoadingState] = useState<LoadingState>("idle");

    const isLoading = loadingState === "loading";
    const hasError = loadingState === "error";

    // Check if iframe loading attribute is supported
    const supportsIframeLoading = useCallback(() => {
      try {
        const iframe = document.createElement("iframe");
        return "loading" in iframe;
      } catch {
        return false;
      }
    }, []);

    const getCertificateUrl = useCallback(() => {
      if (!certificate?.url) return "";
      return `${STORAGE_URL}${certificate.url}`;
    }, [certificate?.url]);

    const handleIframeLoad = useCallback(() => {
      setLoadingState("success");
    }, []);

    const handleIframeError = useCallback(() => {
      setLoadingState("error");
    }, []);

    const handleDownloadCertificate = useCallback(() => {
      if (onDownload) {
        onDownload();
        return;
      }

      const certificateUrl = getCertificateUrl();
      if (!certificateUrl) {
        console.error("Certificate URL not available");
        return;
      }

      try {
        const link = document.createElement("a");
        link.href = certificateUrl;
        link.download = certificate?.filename || "certificado.pdf";
        link.setAttribute("aria-label", `Baixar certificado de ${studentName}`);
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading certificate:", error);
        setLoadingState("error");
      }
    }, [onDownload, getCertificateUrl, certificate?.filename, studentName]);

    const handleClose = useCallback(() => {
      setLoadingState("idle");
      onHide();
    }, [onHide]);

    // Reset states when modal opens
    useEffect(() => {
      if (show && certificate) {
        setLoadingState("loading");
      } else if (!show) {
        setLoadingState("idle");
      }
    }, [show, certificate]);    
    
    const modalVariants = {
      hidden: { opacity: 0, scale: 0.95 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.2, ease: "easeOut" as const },
      },
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.15, ease: "easeIn" as const },
      },
    };

    if (!certificate) return null;

    return (
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        className="certificate-modal"
        backdrop="static"
        keyboard={true}
        aria-labelledby="certificate-modal-title"
        aria-describedby="certificate-modal-description"
      >
        <motion.div
          className="certificate-modal-wrapper"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="certificate-header">
            <div className="certificate-header-content">
              <div className="certificate-title">
                <Icon
                  icon={FaCertificate}
                  className="certificate-icon"
                  aria-hidden={true}
                />
                <h4 id="certificate-modal-title">
                  Certificado - {studentName}
                </h4>
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            className="certificate-content"
            id="certificate-modal-description"
            role="main"
          >
            {isLoading && (
              <div
                className="certificate-loading"
                role="status"
                aria-live="polite"
              >
                <Spinner animation="border" variant="primary" />
                <p>Carregando certificado...</p>
              </div>
            )}

            {hasError && (
              <div
                className="certificate-error"
                role="alert"
                aria-live="assertive"
              >
                <Icon icon={FaExclamationTriangle} className="error-icon" aria-hidden={true} />
                <h5>Erro ao carregar certificado</h5>
                <p>
                  Não foi possível carregar o certificado. Tente fazer o
                  download diretamente.
                </p>
                <Button
                  variant="primary"
                  onClick={handleDownloadCertificate}
                  className="error-download-btn"
                  aria-label={`Baixar certificado de ${studentName}`}
                >
                  <Icon icon={FaDownload} className="me-2" aria-hidden={true} />
                  Baixar Certificado
                </Button>
              </div>
            )}

            {!hasError && (
              <iframe
                src={`${getCertificateUrl()}#toolbar=0&navpanes=0&scrollbar=1`}
                className="certificate-iframe"
                title={`Certificado de ${studentName}`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{ display: isLoading ? "none" : "block" }}
                {...(supportsIframeLoading() && { loading: "lazy" as const })}
              />
            )}
          </div>

          {/* Footer */}
          <div
            className="certificate-footer"
            role="group"
            aria-label="Ações do certificado"
          >
            <Button
              variant="secondary"
              onClick={handleClose}
              className="footer-btn cancel-btn"
              aria-label="Fechar modal do certificado"
            >
              Fechar
            </Button>

            <Button
              variant="success"
              onClick={handleDownloadCertificate}
              className="footer-btn download-btn"
              aria-label={`Baixar certificado de ${studentName}`}
              disabled={loadingState === "loading"}
            >
              <Icon icon={FaDownload} className="me-2" aria-hidden={true} />
              Baixar Certificado
            </Button>
          </div>
        </motion.div>
      </Modal>
    );
  }
);

